import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import some from 'lodash/some';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Portal } from 'react-portal';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { Container, Segment, Button, Message } from 'semantic-ui-react';
import { defineMessages, injectIntl } from 'react-intl';
import Error from '@plone/volto/components/theme/Error/Error';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import backSVG from '@plone/volto/icons/back.svg';
import {
  updateRohstoffkategorie,
  getRohstoffkategorieByID,
  getRohstoffkategorien,
  deleteRohstoffkategorie,
  getVistPermissions,
} from '../../../actions';

import ButtonArea from '../ButtonArea';

import RohstoffkategorieForm from './RohstoffkategorieForm';

const messages = defineMessages({
  back: {
    id: 'Back',
    defaultMessage: 'Back',
  },
});

const RohstoffkategorieEdit = ({
  pathname,
  rohstoffkategorie,
  rohstoffkategorien,
  getRohstoffkategorien,
  getRohstoffkategorieByID,
  updateRohstoffkategorie,
  deleteRohstoffkategorie,
  intl,
  token,
  permissions = null,
  getVistPermissions,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    pid: '',
    id: undefined,
    level: '',
  });

  const location = useLocation();
  const [error, setError] = useState(null);
  const [isClient, setIsClient] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [
    isRohstoffkategorieNameDuplicate,
    setIsRohstoffkategorieNameDuplicate,
  ] = useState(false);
  const isSubmitDisabled = formData.name === '';

  const history = useHistory();
  // Define the allowed user groups for the whole view
  const allowedUserGroups = [1];
  // flag to make sure permissions are fully loaded before returning unauthorized error
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  // load permissions
  useEffect(() => {
    setIsClient(true);
    // Check if permissions are available, if not, fetch them
    if (!permissions.length && permissionsLoading) {
      getVistPermissions()
        .then(() => {
          setPermissionsLoading(false);
        })
        .catch((error) => {
          setError({ status: 401 });
          setPermissionsLoading(false);
        });
    }
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    setIsClient(true);
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');
    getRohstoffkategorien({ showAll: true });
    getRohstoffkategorieByID(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getRohstoffkategorieByID, getRohstoffkategorien]);

  useEffect(() => {
    if (rohstoffkategorie) {
      setFormData({
        name: rohstoffkategorie.name,
        pid: rohstoffkategorie.pid,
        id: rohstoffkategorie.id,
        level: rohstoffkategorie.level,
      });
    }
  }, [rohstoffkategorie]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));

    if (name === 'name' && value !== rohstoffkategorie.name) {
      const isDuplicate = rohstoffkategorien.some(
        (rohstoffkategorie) => rohstoffkategorie.name === value,
      );
      setIsRohstoffkategorieNameDuplicate(isDuplicate);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');

    try {
      await updateRohstoffkategorie(id, formData);
      history.push(`/controlpanel/rezeptdatenbank/rohstoffkategorien`);
      // Handle success or show a success message
    } catch (error) {
      // Handle error during update
      setError(error);
    }
  };

  const handleDelete = async () => {
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');

    try {
      await deleteRohstoffkategorie(id);
      setDeleteSuccess(true);
    } catch (error) {
      // Handle delete error
      setError(error);
    }
  };

  if (!token) {
    return <Error error={{ status: 401 }} />;
  }
  if (
    !permissionsLoading &&
    !some(allowedUserGroups, (group) => permissions.includes(group))
  ) {
    return <Error error={{ status: 401 }} />;
  }
  return (
    <div className="view-wrapper">
      <Helmet title="Rohstoffkategorie Bearbeiten" />
      <Container className="controlpanel">
        <Segment.Group raised>
          <Segment className="primary">
            <div className="rezeptdatenbank header">
              <div className="header-container">
                <h1>
                  {rohstoffkategorie && rohstoffkategorie.name} Bearbeiten
                </h1>
              </div>
              <div className="button-container">
                <ButtonArea
                  handleSubmit={handleSubmit}
                  handleDelete={handleDelete}
                  isSubmitDisabled={isSubmitDisabled}
                  backURL={'/controlpanel/rezeptdatenbank/rohstoffkategorien'}
                  typeString={'die Rohstoffkategorie'}
                />
              </div>
            </div>
            <div className="rezeptdatenbank header"></div>
          </Segment>
          <Segment className="rdbAddEditForm">
            {error && (
              <Message negative>
                <Message.Header>Error</Message.Header>
                <p>{error.message}</p>
              </Message>
            )}
            {deleteSuccess ? (
              <Message positive>
                <Message.Header>
                  Rohstoffkategorie wurde gelöscht
                </Message.Header>
                <Button
                  primary
                  as={Link}
                  to="/controlpanel/rezeptdatenbank/rohstoffkategorien"
                >
                  Zurück zur Rohstoffkategorien-Übersicht
                </Button>
              </Message>
            ) : (
              <>
                <div className="rdb edit form rohstoffkategorien">
                  <RohstoffkategorieForm
                    formData={formData}
                    isRohstoffkategorieNameDuplicate={
                      isRohstoffkategorieNameDuplicate
                    }
                    handleInputChange={handleInputChange}
                    kategorien={rohstoffkategorien}
                  />
                </div>
              </>
            )}
          </Segment>
        </Segment.Group>
      </Container>
      {isClient && (
        <Portal node={document.getElementById('toolbar')}>
          <Toolbar
            pathname={pathname}
            hideDefaultViewButtons
            inner={
              <Link to="/controlpanel/rezeptdatenbank" className="item">
                <Icon
                  name={backSVG}
                  className="contents circled"
                  size="30px"
                  title={intl.formatMessage(messages.back)}
                />
              </Link>
            }
          />
        </Portal>
      )}
    </div>
  );
};

RohstoffkategorieEdit.propTypes = {
  pathname: PropTypes.string.isRequired,
  rohstoffkategorien: PropTypes.array.isRequired,
  rohstoffkategorie: PropTypes.object,
  getRohstoffkategorieByID: PropTypes.func.isRequired,
  updateRohstoffkategorie: PropTypes.func.isRequired,
  deleteRohstoffkategorie: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  token: PropTypes.string,
  permissions: PropTypes.array,
  getVistPermissions: PropTypes.func,
};

export default compose(
  injectIntl,
  connect(
    (state) => ({
      pathname: state.router.location.pathname,
      rohstoffkategorie: state.rohstoffkategorieByID.data,
      rohstoffkategorien: state.rohstoffkategorien.data.rohstoffkategorien,
      token: state.userSession.token,
      permissions: state.permissions.data,
    }),
    {
      updateRohstoffkategorie,
      getRohstoffkategorieByID,
      deleteRohstoffkategorie,
      getRohstoffkategorien,
      getVistPermissions,
    },
  ),
)(RohstoffkategorieEdit);
